import React, { useState } from "react";
import { motion } from "framer-motion";
import { Search, Coins, Gift, Users, Shield, Gamepad2, Hash, Settings, Info } from "lucide-react";

const Commands = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const categories = [
    { id: "all", name: "All Commands", icon: Hash, color: "from-gray-400 to-gray-500" },
    { id: "economy", name: "Economy", icon: Coins, color: "from-yellow-400 to-orange-500" },
    { id: "games", name: "Games", icon: Gamepad2, color: "from-red-400 to-pink-500" },
    { id: "giveaway", name: "Giveaways", icon: Gift, color: "from-pink-400 to-purple-500" },
    { id: "fun", name: "Fun", icon: Users, color: "from-green-400 to-teal-500" },
    { id: "utility", name: "Utility", icon: Settings, color: "from-blue-400 to-indigo-500" },
    { id: "welcome", name: "Welcome", icon: Users, color: "from-purple-400 to-blue-500" },
    { id: "counting", name: "Counting", icon: Hash, color: "from-teal-400 to-green-500" },
    { id: "toproll", name: "Toproll", icon: Gamepad2, color: "from-orange-400 to-red-500" },
    { id: "suggest", name: "Suggestions", icon: Info, color: "from-indigo-400 to-purple-500" },
    { id: "notifications", name: "Notifications", icon: Settings, color: "from-cyan-400 to-blue-500" },
    { id: "voxel", name: "Bot Info", icon: Info, color: "from-slate-400 to-gray-500" },
  ];

  const commands = [
    // Economy Commands
    {
      name: "/bank view",
      description: "View your wallet and bank balance",
      usage: "/bank view",
      category: "economy",
      example: "/bank view"
    },
    {
      name: "/bank deposit",
      description: "Deposit money from your wallet into your bank",
      usage: "/bank deposit <amount>",
      category: "economy",
      example: "/bank deposit 1000"
    },
    {
      name: "/bank withdraw",
      description: "Withdraw money from your bank into your wallet",
      usage: "/bank withdraw <amount>",
      category: "economy",
      example: "/bank withdraw 500"
    },
    {
      name: "/beg",
      description: "Beg for coins... maybe someone will be generous!",
      usage: "/beg",
      category: "economy",
      example: "/beg"
    },
    {
      name: "/blackjack",
      description: "Play a game of blackjack",
      usage: "/blackjack <bet>",
      category: "economy",
      example: "/blackjack 100"
    },
    {
      name: "/business trade",
      description: "Buy or sell a business",
      usage: "/business trade",
      category: "economy",
      example: "/business trade"
    },
    {
      name: "/business view",
      description: "View your owned businesses",
      usage: "/business view",
      category: "economy",
      example: "/business view"
    },
    {
      name: "/business collect",
      description: "Collect your business earnings (non-premium only)",
      usage: "/business collect",
      category: "economy",
      example: "/business collect"
    },
    {
      name: "/crime",
      description: "Attempt a crime by staking money!",
      usage: "/crime <amount>",
      category: "economy",
      example: "/crime 250"
    },
    {
      name: "/daily",
      description: "Claim your daily reward and keep your streak!",
      usage: "/daily",
      category: "economy",
      example: "/daily"
    },
    {
      name: "/dailyspin",
      description: "Spin the wheel once a day to win prizes!",
      usage: "/dailyspin",
      category: "economy",
      example: "/dailyspin"
    },
    {
      name: "/dice-duel",
      description: "Challenge another user to a dice duel",
      usage: "/dice-duel <user>",
      category: "economy",
      example: "/dice-duel @user"
    },
    {
      name: "/donate",
      description: "Donate money from your wallet or bank to another user's wallet",
      usage: "/donate <user> <amount>",
      category: "economy",
      example: "/donate @user 500"
    },
    {
      name: "/doors",
      description: "Play a game of doors for a chance to win coins!",
      usage: "/doors <bet>",
      category: "economy",
      example: "/doors 100"
    },
    {
      name: "/heist",
      description: "Start a collaborative heist!",
      usage: "/heist",
      category: "economy",
      example: "/heist"
    },
    {
      name: "/hilo",
      description: "Play HiLo for money!",
      usage: "/hilo <bet>",
      category: "economy",
      example: "/hilo 150"
    },
    {
      name: "/horserace",
      description: "Bet on a horse for a chance to win big!",
      usage: "/horserace <bet>",
      category: "economy",
      example: "/horserace 200"
    },
    {
      name: "/leaderboard",
      description: "View the top 20 richest users",
      usage: "/leaderboard",
      category: "economy",
      example: "/leaderboard"
    },
    {
      name: "/lotto status",
      description: "View current lotto status",
      usage: "/lotto status",
      category: "economy",
      example: "/lotto status"
    },
    {
      name: "/lotto ticket",
      description: "Buy lotto tickets with bank balance",
      usage: "/lotto ticket <amount>",
      category: "economy",
      example: "/lotto ticket 5"
    },
    {
      name: "/marketcrash",
      description: "Bet on the market multiplier before it crashes!",
      usage: "/marketcrash <bet>",
      category: "economy",
      example: "/marketcrash 300"
    },
    {
      name: "/minesweeper",
      description: "Play Minesweeper for money!",
      usage: "/minesweeper <bet>",
      category: "economy",
      example: "/minesweeper 75"
    },
    {
      name: "/profile",
      description: "View your profile or someone else's",
      usage: "/profile [user]",
      category: "economy",
      example: "/profile @user"
    },
    {
      name: "/roulette",
      description: "Play a game of roulette!",
      usage: "/roulette <bet> <choice>",
      category: "economy",
      example: "/roulette 100 red"
    },
    {
      name: "/slots",
      description: "Spin the slot machine for a chance to win coins!",
      usage: "/slots <bet>",
      category: "economy",
      example: "/slots 50"
    },
    {
      name: "/steal user",
      description: "Steal from someone in your server",
      usage: "/steal user <target>",
      category: "economy",
      example: "/steal user @user"
    },
    {
      name: "/stock market",
      description: "View all stocks",
      usage: "/stock market",
      category: "economy",
      example: "/stock market"
    },
    {
      name: "/stock buy",
      description: "Buy stock shares",
      usage: "/stock buy <symbol> <shares>",
      category: "economy",
      example: "/stock buy AAPL 10"
    },
    {
      name: "/work start",
      description: "Work your job and earn coins",
      usage: "/work start",
      category: "economy",
      example: "/work start"
    },

    // Games Commands
    {
      name: "/connectfour",
      description: "Challenge another user to a Connect Four game",
      usage: "/connectfour <opponent>",
      category: "games",
      example: "/connectfour @user"
    },
    {
      name: "/quiz",
      description: "Starts a multiple-choice quiz game with 3 options!",
      usage: "/quiz",
      category: "games",
      example: "/quiz"
    },
    {
      name: "/rps",
      description: "Challenge another user to a Rock-Paper-Scissors duel",
      usage: "/rps <opponent>",
      category: "games",
      example: "/rps @user"
    },
    {
      name: "/tictactoe",
      description: "Challenge another user to Tic Tac Toe!",
      usage: "/tictactoe <opponent>",
      category: "games",
      example: "/tictactoe @user"
    },
    {
      name: "/wordle",
      description: "Play a game of Wordle",
      usage: "/wordle",
      category: "games",
      example: "/wordle"
    },
    {
      name: "/wyr",
      description: "Starts a Would You Rather mini game!",
      usage: "/wyr",
      category: "games",
      example: "/wyr"
    },

    // Giveaway Commands
    {
      name: "/giveaway create",
      description: "Create a new giveaway",
      usage: "/giveaway create <prize> <duration> [requirements]",
      category: "giveaway",
      example: "/giveaway create 'Discord Nitro' 1d"
    },
    {
      name: "/giveaway end",
      description: "End an ongoing giveaway",
      usage: "/giveaway end <message_id>",
      category: "giveaway",
      example: "/giveaway end 123456789"
    },
    {
      name: "/giveaway reroll",
      description: "Reroll a giveaway winner",
      usage: "/giveaway reroll <message_id>",
      category: "giveaway",
      example: "/giveaway reroll 123456789"
    },
    {
      name: "/giveaway entries",
      description: "List the entries of a giveaway",
      usage: "/giveaway entries <message_id>",
      category: "giveaway",
      example: "/giveaway entries 123456789"
    },

    // Fun Commands
    {
      name: "/cookie give",
      description: "Give a cookie to someone!",
      usage: "/cookie give <user>",
      category: "fun",
      example: "/cookie give @user"
    },
    {
      name: "/cookie me",
      description: "Check how many cookies you have received!",
      usage: "/cookie me",
      category: "fun",
      example: "/cookie me"
    },
    {
      name: "/dadjoke",
      description: "Get a random dad joke!",
      usage: "/dadjoke",
      category: "fun",
      example: "/dadjoke"
    },

    // Counting Commands
    {
      name: "/counting channel",
      description: "Select a channel for the counting game",
      usage: "/counting channel <channel>",
      category: "counting",
      example: "/counting channel #counting"
    },
    {
      name: "/counting highscore",
      description: "Show the server's counting game high score",
      usage: "/counting highscore",
      category: "counting",
      example: "/counting highscore"
    },
    {
      name: "/counting stop",
      description: "Stop the counting game and remove the channel slow mode",
      usage: "/counting stop",
      category: "counting",
      example: "/counting stop"
    },
    {
      name: "/countprofile",
      description: "Shows your counting profile",
      usage: "/countprofile [user]",
      category: "counting",
      example: "/countprofile @user"
    },

    // Toproll Commands
    {
      name: "/roll",
      description: "Roll for the toproll game",
      usage: "/roll",
      category: "toproll",
      example: "/roll"
    },
    {
      name: "/toproll setup",
      description: "Set the toproll announcement channel",
      usage: "/toproll setup <channel>",
      category: "toproll",
      example: "/toproll setup #announcements"
    },
    {
      name: "/toproll start",
      description: "Starts a toproll game",
      usage: "/toproll start",
      category: "toproll",
      example: "/toproll start"
    },

    // Welcome Commands
    {
      name: "/welcome",
      description: "View and manage the welcome message configuration for the server",
      usage: "/welcome [setup/message/toggle]",
      category: "welcome",
      example: "/welcome setup #general"
    },

    // Suggestion Commands
    {
      name: "/suggest",
      description: "Submit a new suggestion",
      usage: "/suggest <suggestion>",
      category: "suggest",
      example: "/suggest Add more games!"
    },
    {
      name: "/approve",
      description: "Approve an existing suggestion by its message ID (Admins only)",
      usage: "/approve <message_id>",
      category: "suggest",
      example: "/approve 123456789"
    },
    {
      name: "/reject",
      description: "Reject an existing suggestion by its message ID (Admins only)",
      usage: "/reject <message_id>",
      category: "suggest",
      example: "/reject 123456789"
    },

    // Notifications Commands
    {
      name: "/twitch",
      description: "Manage Twitch notifications for your server",
      usage: "/twitch [setup/remove]",
      category: "notifications",
      example: "/twitch setup"
    },
    {
      name: "/youtube",
      description: "View and manage YouTube notification settings for this server",
      usage: "/youtube [setup/remove]",
      category: "notifications",
      example: "/youtube setup"
    },

    // Utility Commands
    {
      name: "/embed",
      description: "Manage and post embeds in the server",
      usage: "/embed [create/edit]",
      category: "utility",
      example: "/embed create"
    },
    {
      name: "/logs",
      description: "View and manage logging configuration for the server",
      usage: "/logs [setup/disable]",
      category: "utility",
      example: "/logs setup"
    },
    {
      name: "/temprole",
      description: "Temporarily assign a role to a user",
      usage: "/temprole <user> <role> <duration>",
      category: "utility",
      example: "/temprole @user @Muted 1h"
    },

    // Bot Info Commands
    {
      name: "/help",
      description: "Lists all available commands",
      usage: "/help [command]",
      category: "voxel",
      example: "/help balance"
    },
    {
      name: "/info bot",
      description: "Shows bot uptime, latency, server count, user count, and commands used",
      usage: "/info bot",
      category: "voxel",
      example: "/info bot"
    },
    {
      name: "/premium",
      description: "Check your premium status",
      usage: "/premium",
      category: "voxel",
      example: "/premium"
    },
    {
      name: "/support",
      description: "Join the support server and learn about activity rewards",
      usage: "/support",
      category: "voxel",
      example: "/support"
    },
    {
      name: "/vote",
      description: "Check your vote status and learn how to support the bot!",
      usage: "/vote",
      category: "voxel",
      example: "/vote"
    }
  ];

  const filteredCommands = commands.filter(command => {
    const matchesSearch = command.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         command.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || command.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial="initial"
          animate="animate"
          variants={stagger}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Bot Commands
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Explore all the commands Voxel has to offer for your Discord server
            </p>
          </motion.div>

          {/* Search and Filter */}
          <motion.div variants={fadeInUp} className="mb-8">
            <div className="relative max-w-md mx-auto mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search commands..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                      : "bg-slate-800 text-gray-300 hover:bg-slate-700"
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  {category.name}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Commands Grid */}
          <motion.div
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredCommands.map((command, index) => {
              const category = categories.find(cat => cat.id === command.category);
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-10 h-10 bg-gradient-to-r ${category?.color || 'from-gray-400 to-gray-500'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      {category && <category.icon className="w-5 h-5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-blue-400 font-mono break-all">
                        {command.name}
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${category?.color || 'from-gray-400 to-gray-500'} text-white`}>
                        {category?.name || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                    {command.description}
                  </p>
                  
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500 uppercase font-medium">Usage:</span>
                      <code className="block mt-1 text-sm bg-slate-800 px-3 py-2 rounded-lg text-green-400 font-mono break-all">
                        {command.usage}
                      </code>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 uppercase font-medium">Example:</span>
                      <code className="block mt-1 text-sm bg-slate-800 px-3 py-2 rounded-lg text-blue-400 font-mono break-all">
                        {command.example}
                      </code>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {filteredCommands.length === 0 && (
            <motion.div
              variants={fadeInUp}
              className="text-center py-12"
            >
              <p className="text-gray-400 text-lg">No commands found matching your search.</p>
            </motion.div>
          )}

          {/* CTA */}
          <motion.div
            variants={fadeInUp}
            className="text-center mt-16 p-8 bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-2xl border border-blue-500/20"
          >
            <h3 className="text-2xl font-bold mb-4">Ready to get started?</h3>
            <p className="text-gray-300 mb-6">Add Voxel to your server and start using these commands today!</p>
            <a
              href="https://discord.com/oauth2/authorize?client_id=1323444656070983701"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
            >
              Add to Server
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Commands;
